/**
 * Document 2 · Phase 10.3a — Public /pricing page
 *
 * Route is accessible pre-login. If user not authenticated, clicking a tier
 * sends them to /register?plan=<id>&interval=<m|y> (they return here after).
 *
 * If authenticated, POST /api/billing/checkout → redirect to Stripe hosted
 * Checkout in subscription mode.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, CheckCircle2, Shield, TrendingUp, Loader2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';


const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;


function Feature({ text }) {
  return (
    <li className="flex items-start gap-2 text-sm" style={{ color: '#1f2937' }}>
      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#B89B5E' }} />
      <span>{text}</span>
    </li>
  );
}


export default function Pricing() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState('monthly'); // default monthly — users toggle to yearly to see savings
  const [checkingOut, setCheckingOut] = useState(null); // tier being processed

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/billing/plans`);
        setPlans(r.data.plans || []);
      } catch (e) {
        toast.error('Failed to load plans');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const annualSavings = useMemo(() => (monthly, yearly) => {
    const yearlyAsMonthly = monthly * 12;
    if (!yearlyAsMonthly) return 0;
    return Math.round(((yearlyAsMonthly - yearly) / yearlyAsMonthly) * 100);
  }, []);

  const handleStart = async (plan) => {
    if (!user || !token) {
      // Paid signup flow: /signup → register → auto-login → Stripe checkout.
      // We pass plan+interval so the new page can stage the checkout call.
      navigate(`/signup?plan=${plan.id}&interval=${interval}`);
      return;
    }
    setCheckingOut(plan.id);
    // Belt-and-suspenders: send the price_id we just SHOWED on this page.
    // Backend confirms it still matches the admin-configured one. If admin
    // changed prices since this page loaded, backend returns 409 price_changed.
    const expectedPriceId = interval === 'yearly' ? plan.yearly_price_id : plan.monthly_price_id;
    try {
      const r = await axios.post(`${API}/billing/checkout`,
        {
          tier: plan.id,
          interval,
          success_path: '/billing/return',
          cancel_path: '/pricing',
          ...(expectedPriceId ? { expected_price_id: expectedPriceId } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Could not start checkout — missing checkout URL');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Special handling: prices changed between page load and click
      if (detail && typeof detail === 'object' && detail.reason === 'price_changed') {
        toast.error('Pricing has been updated. Refreshing…');
        setTimeout(() => window.location.reload(), 1500);
        return;
      }
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Checkout failed');
      toast.error(msg);
      setCheckingOut(null);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: 'linear-gradient(180deg, #fefefe 0%, #f5f0e8 100%)' }}
      data-testid="pricing-page"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#B89B5E]/40 bg-white text-xs font-medium mb-4" style={{ color: '#B89B5E' }}>
            <Sparkles className="w-3.5 h-3.5" />
            14-day free trial · cancel anytime
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-4" data-testid="pricing-heading">
            Choose your growth tier
          </h1>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Start your 14-day free trial. Card required — not charged until the trial ends. Cancel anytime.
            Every plan includes unlimited deck views and custom branding on shared links.
          </p>
          {!user && (
            <p className="text-sm text-muted-foreground mt-4">
              Already have an account?{' '}
              <Link
                to="/login"
                className="underline underline-offset-2 hover:text-foreground transition"
                style={{ color: '#B89B5E' }}
                data-testid="pricing-signin-link-top"
              >
                Sign in →
              </Link>
            </p>
          )}
        </div>

        {/* Interval toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center rounded-full border bg-white p-1" data-testid="pricing-interval-toggle">
            <button
              type="button"
              onClick={() => setInterval('monthly')}
              className={`px-4 py-1.5 rounded-full text-sm transition ${
                interval === 'monthly'
                  ? 'bg-[#B89B5E] text-white font-semibold'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="pricing-interval-monthly"
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setInterval('yearly')}
              className={`px-4 py-1.5 rounded-full text-sm transition flex items-center gap-1.5 ${
                interval === 'yearly'
                  ? 'bg-[#B89B5E] text-white font-semibold'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="pricing-interval-yearly"
            >
              Yearly
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${
                interval === 'yearly' ? 'bg-white/25' : 'bg-emerald-100 text-emerald-700'
              }`}>
                Save ~17%
              </span>
            </button>
          </div>
        </div>

        {/* Tier cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const price = interval === 'monthly' ? plan.monthly_price : plan.annual_price;
              const isHighlight = plan.highlight;
              const savingsPct = annualSavings(plan.monthly_price, plan.annual_price);
              return (
                <Card
                  key={plan.id}
                  className={`rounded-sm border relative flex flex-col ${
                    isHighlight ? 'border-[#B89B5E] shadow-[0_12px_32px_rgba(184,155,94,0.15)]' : ''
                  }`}
                  style={{
                    transform: isHighlight ? 'translateY(-8px)' : 'none',
                    borderWidth: isHighlight ? 2 : 1,
                  }}
                  data-testid={`pricing-card-${plan.id}`}
                >
                  {isHighlight && (
                    <div
                      className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-white whitespace-nowrap"
                      style={{ backgroundColor: '#B89B5E' }}
                    >
                      <Shield className="w-3 h-3 inline mr-1" /> Most popular
                    </div>
                  )}
                  <CardContent className="p-6 md:p-8 flex flex-col h-full">
                    <div className="mb-5">
                      <div className="text-xs uppercase tracking-widest mb-2" style={{ color: '#B89B5E' }}>
                        {plan.name}
                      </div>
                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="font-serif text-5xl font-bold leading-none" style={{ color: '#1f2937' }}>
                          ${price.toLocaleString()}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          /{interval === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      </div>
                      {interval === 'yearly' && savingsPct > 0 && (
                        <Badge variant="outline" className="rounded-sm border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px] mt-2">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Save {savingsPct}% vs monthly
                        </Badge>
                      )}
                    </div>

                    <ul className="space-y-2 flex-1 mb-6">
                      {plan.features.map((f, i) => <Feature key={i} text={f} />)}
                    </ul>

                    <Button
                      className="w-full rounded-sm"
                      size="lg"
                      onClick={() => handleStart(plan)}
                      disabled={checkingOut === plan.id}
                      style={
                        isHighlight
                          ? { backgroundColor: '#B89B5E', color: 'white' }
                          : { backgroundColor: 'white', color: '#B89B5E', border: '1.5px solid #B89B5E' }
                      }
                      data-testid={`pricing-cta-${plan.id}`}
                    >
                      {checkingOut === plan.id
                        ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Redirecting…</>)
                        : (user ? 'Start checkout' : 'Start free trial — no charge for 14 days')
                      }
                    </Button>
                    <div className="text-[10px] text-center text-muted-foreground mt-2">
                      {user
                        ? 'Secure checkout via Stripe'
                        : 'Card required · not charged for 14 days · cancel anytime'}
                    </div>
                    {!user && (
                      <div className="text-center mt-1.5">
                        <Link
                          to="/login"
                          className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2 transition"
                          data-testid={`pricing-signin-link-${plan.id}`}
                        >
                          Already have an account? Sign in
                        </Link>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center text-xs text-muted-foreground">
          All prices in USD. Cancel anytime. Payments processed by Stripe.
        </div>
      </div>
    </div>
  );
}
